import React from "react";
import { Card, Row, Col, Statistic } from "antd";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  MobileOutlined,
  DesktopOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import { motion } from "framer-motion";

const COLORS = {
  devices: ["#1890FF", "#52C41A", "#722ED1", "#FA8C16"],
  emailClients: ["#13C2C2", "#EB2F96", "#2F54EB", "#FADB14"],
  os: ["#F5222D", "#FA8C16", "#52C41A", "#1890FF"],
};

const DeviceAnalytics = ({ deviceData }) => {
  if (!deviceData) return null;

  // Transform data for charts
  const deviceChartData = Object.entries(deviceData.devices || {}).map(
    ([name, value]) => ({
      name,
      value,
    })
  );

  const emailClientChartData = Object.entries(
    deviceData.emailClients || {}
  ).map(([name, value]) => ({
    name,
    value,
  }));

  const osChartData = Object.entries(deviceData.operatingSystems || {}).map(
    ([name, value]) => ({
      name,
      value,
    })
  );

  // Calculate totals for percentages
  const totalDevices = deviceChartData.reduce(
    (sum, item) => sum + item.value,
    0
  );
  const totalClients = emailClientChartData.reduce(
    (sum, item) => sum + item.value,
    0
  );
  const totalOS = osChartData.reduce((sum, item) => sum + item.value, 0);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            background: "white",
            padding: "12px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <p style={{ margin: 0, fontWeight: "bold" }}>{payload[0].name}</p>
          <p style={{ margin: "4px 0 0" }}>
            Count: {payload[0].value}
            <br />
            Percentage: {((payload[0].value / totalDevices) * 100).toFixed(1)}%
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Row gutter={[24, 24]}>
      {/* Summary Statistics */}
      <Col xs={24} xl={8}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card bordered={false}>
            <Statistic
              title="Unique Devices"
              value={Object.keys(deviceData.devices || {}).length}
              prefix={<MobileOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </motion.div>
      </Col>
      <Col xs={24} xl={8}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <Card bordered={false}>
            <Statistic
              title="Email Clients"
              value={Object.keys(deviceData.emailClients || {}).length}
              prefix={<InboxOutlined />}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </motion.div>
      </Col>
      <Col xs={24} xl={8}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Card bordered={false}>
            <Statistic
              title="Operating Systems"
              value={Object.keys(deviceData.operatingSystems || {}).length}
              prefix={<DesktopOutlined />}
              valueStyle={{ color: "#722ed1" }}
            />
          </Card>
        </motion.div>
      </Col>

      {/* Device Distribution */}
      <Col xs={24} lg={12}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Card title="Device Distribution" bordered={false}>
            <div style={{ height: "400px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={deviceChartData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={120}
                    label={({ name, percent }) =>
                      `${name} (${(percent * 100).toFixed(1)}%)`
                    }
                  >
                    {deviceChartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS.devices[index % COLORS.devices.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </motion.div>
      </Col>

      {/* Email Client Distribution */}
      <Col xs={24} lg={12}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <Card title="Email Client Usage" bordered={false}>
            <div style={{ height: "400px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={emailClientChartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={100} />
                  <Tooltip />
                  <Bar
                    dataKey="value"
                    fill="#13C2C2"
                    label={{ position: "right", fill: "#666" }}
                    barSize={20}
                  >
                    {emailClientChartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          COLORS.emailClients[
                            index % COLORS.emailClients.length
                          ]
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </motion.div>
      </Col>

      {/* Operating System Distribution */}
      <Col xs={24}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <Card title="Operating System Distribution" bordered={false}>
            <div style={{ height: "300px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={osChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" fill="#1890FF">
                    {osChartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS.os[index % COLORS.os.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </motion.div>
      </Col>
    </Row>
  );
};

export default DeviceAnalytics;
